import Raw from 'components/raw/raw';
import { useCurrentBilikPerson } from 'hooks/use-current-bilik-person/use-current-bilik-person';
import { ModerateReviewReplyActions } from 'modules/review/components/ModerateReviewReplyActions';
import ToggleLockReviewButton from 'modules/review/components/ToggleLockReviewButton';
import React, { FunctionComponent } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import styled from 'styled-components';
import { ToastSuccess } from 'utils';
import { useReviewModals } from '../hooks/useReviewModals';
import { Review } from '../types/review.type';
import {
  ModerationSection,
  ModerationSectionActions,
  ModerationSectionContent,
} from './ModerationSection';
import { ReviewActionsModal } from './ReviewActionsModal';
import { ReviewReplyHeader } from './ReviewReplyHeader';
import { hasOnlyArchivedViews } from '../utils/hasOnlyArchivedViews';

const EmptyText = styled.div`
  color: rgba(0, 0, 0, 0.45);
  font-style: italic;
  margin-bottom: 14px;
`;

type ReviewReplySectionProps = {
  review: Review;
  onReviewChange: () => void;
};

export const ReviewReplySection: FunctionComponent<ReviewReplySectionProps> = ({
  review,
  onReviewChange,
}) => {
  const { currentBilikPerson } = useCurrentBilikPerson();
  const reviewHasOnlyArchivedViews = hasOnlyArchivedViews(review);
  const isReviewLockedByOther =
    !!review.locker && review.locker.id !== currentBilikPerson?.id;
  const isLockedOrArchived =
    isReviewLockedByOther || reviewHasOnlyArchivedViews;
  const { modalMetadata, openReplyModal, openReplyEditModal, closeModal } =
    useReviewModals();

  // Reply moderation is only for BILIK non-refused non-spam reviews.
  const hasModerationActions =
    review.status !== 'ko' &&
    !review.isSpam &&
    review.source === 'BILIK' &&
    (!!review.replyStatus || !review.reply);
  const hasEditAction = !!review.reply;
  const hasLockAction =
    review.status === 'ok' && !!review.reply && review.replyStatus === 'draft';
  const hasAnyAction = hasModerationActions || hasEditAction || hasLockAction;

  return (
    <ModerationSection>
      <ReviewReplyHeader review={review} />
      <ModerationSectionContent>
        {review.reply ? (
          <p>
            <Raw>{review.reply}</Raw>
          </p>
        ) : (
          <EmptyText>Aucune réponse du pro.</EmptyText>
        )}
      </ModerationSectionContent>
      {hasAnyAction ? (
        <ModerationSectionActions>
          <Popup
            content={
              reviewHasOnlyArchivedViews
                ? 'Fiche archivée'
                : isReviewLockedByOther
                  ? 'Avis verrouillé'
                  : review.status === 'draft'
                    ? "Veuillez d'abord modérer l'avis avant de modérer la réponse"
                    : null
            }
            disabled={!isLockedOrArchived && review.status !== 'draft'}
            trigger={
              <span>
                {hasModerationActions ? (
                  <>
                    <ModerateReviewReplyActions
                      reviewId={review.id}
                      reviewStatus={review.status}
                      reply={review.reply}
                      replyStatus={review.replyStatus}
                      disabled={isLockedOrArchived}
                      onModerateSuccess={(type) => {
                        ToastSuccess(
                          'Succès',
                          `Réponse ${
                            type === 'validate' ? 'validée' : 'refusée'
                          }`,
                        );
                        onReviewChange();
                      }}
                    />
                    {!review.reply ? (
                      <Popup
                        content={'Répondre'}
                        trigger={
                          <Button
                            color="blue"
                            basic
                            disabled={
                              isLockedOrArchived || review.status === 'draft'
                            }
                            onClick={(): void => openReplyModal(review)}
                          >
                            <Icon name="reply" />
                            Répondre
                          </Button>
                        }
                      />
                    ) : null}
                  </>
                ) : null}
              </span>
            }
          />
          {hasEditAction ? (
            <Popup
              content={
                reviewHasOnlyArchivedViews
                  ? 'Fiche archivée — modération désactivée'
                  : 'Éditer'
              }
              trigger={
                <Button
                  onClick={() => openReplyEditModal(review)}
                  color="blue"
                  icon
                  basic
                  disabled={reviewHasOnlyArchivedViews}
                >
                  <Icon name="pencil" />
                </Button>
              }
            />
          ) : null}
          {hasLockAction ? (
            <ToggleLockReviewButton
              reviewId={review.id}
              disabled={isLockedOrArchived}
              onComplete={() => {
                onReviewChange();
              }}
            />
          ) : null}
        </ModerationSectionActions>
      ) : null}
      <ReviewActionsModal
        closeModal={closeModal}
        modalMetadata={modalMetadata}
        onReviewChange={onReviewChange}
      />
    </ModerationSection>
  );
};
